'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import UploadZone from '@/components/pdf/UploadZone';
import { TOOL_COLORS } from '@/constants/toolColors';
import { useToast } from '@/components/ui/Toast';
import { isEncryptedPDF } from '@/utils/pdf';
import { FileCard } from '@/components/pdf/FileCard';
import { getPdfMeta, useEncryptedPdfToast } from '@/constants/pdfConfig';

const colors = TOOL_COLORS.compress;

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  file: File;
  pageCount: number;
}

type StepStatus = 'idle' | 'active' | 'done' | 'error';

interface ProcessStep {
  id: string;
  label: string;
  detail: string;
  status: StepStatus;
}

type CompressionMode = 'low' | 'medium' | 'high';

const INITIAL_STEPS: ProcessStep[] = [
  {
    id: 'upload',
    label: 'Uploading PDF',
    detail: 'Securely transferring to server — no files are stored',
    status: 'idle',
  },
  {
    id: 'compress',
    label: 'Compressing PDF',
    detail: 'Optimizing images and fonts to reduce file size',
    status: 'idle',
  },
  {
    id: 'optimize',
    label: 'Finalizing & Optimizing',
    detail: 'Preparing your compressed file for download',
    status: 'idle',
  },
];

const API_BASE = process.env.API_BASE_URL || 'https://api.luvupdf.com';

const COMPRESS_THEME = {
  primary: '#10B981',
  primaryLight: '#ECFDF5',
  primaryBorder: '#A7F3D0',
  primaryShadow: 'rgba(16, 185, 129, 0.28)',
};

export default function CompressPDFUploader() {
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [mode, setMode] = useState<CompressionMode>('medium');
  const [isDone, setIsDone] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [steps, setSteps] = useState<ProcessStep[]>(INITIAL_STEPS);
  const [overallProgress, setOverallProgress] = useState(0);
  const [results, setResults] = useState<{
    originalSize: string;
    compressedSize: string;
    reductionPercent: string;
    pdfUrl: string;
  } | null>(null);

  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { showToast } = useToast();
  const { showEncryptedPdfToast } = useEncryptedPdfToast();

  const animateToProgress = (target: number) => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(() => {
      setOverallProgress((prev) => {
        if (prev >= target) {
          clearInterval(progressTimerRef.current!);
          return target;
        }
        return Math.min(prev + 1, target);
      });
    }, 20);
  };

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, []);

  const setStepStatus = (stepId: string, status: StepStatus) => {
    setSteps((prev) => prev.map((s) => (s.id === stepId ? { ...s, status } : s)));
  };

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

  const handleFilesSelected = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const f = files[0];
      if (!f.name.endsWith('.pdf') && f.type !== 'application/pdf') {
        showToast('Invalid file type. Please upload a PDF.', 'error');
        return;
      }
      if (f.size > MAX_FILE_SIZE) {
        showToast(`File too large. Maximum allowed size is 50 MB.`, 'error');
        return;
      }

      const alreadyEncrypted = await isEncryptedPDF(f);
      if (alreadyEncrypted) {
        showEncryptedPdfToast('compressing');
        return;
      }

      const meta = await getPdfMeta(f);
      setFile(meta);
      setIsDone(false);
      setSteps(INITIAL_STEPS);
      setOverallProgress(0);
    },
    [showToast]
  );

  const handleCompress = async () => {
    if (!file) return;

    setIsProcessing(true);
    setSteps(INITIAL_STEPS);
    setOverallProgress(0);

    try {
      // 1. Pre-flight check
      setStepStatus('upload', 'active');
      animateToProgress(10);

      // 2. Upload
      animateToProgress(30);
      const formData = new FormData();
      formData.append('file', file.file);

      const response = await fetch(`${API_BASE}/api/pdf/compress-pdf/${mode}`, {
        method: 'POST',
        body: formData,
      });

      setStepStatus('upload', 'done');
      setStepStatus('compress', 'active');
      animateToProgress(70);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Compression failed');
      }

      setStepStatus('compress', 'done');
      setStepStatus('optimize', 'active');
      animateToProgress(95);

      setResults({
        originalSize: data.originalSize,
        compressedSize: data.compressedSize,
        reductionPercent: data.reductionPercent,
        pdfUrl: data.pdfUrl,
      });

      animateToProgress(100);
      await new Promise((r) => setTimeout(r, 500));
      setStepStatus('optimize', 'done');
      setIsDone(true);
      showToast('PDF compressed successfully!', 'success');
    } catch (err: any) {
      showToast(err.message || 'An error occurred during compression', 'error');
      setSteps((prev) => prev.map((s) => (s.status === 'active' ? { ...s, status: 'error' } : s)));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setIsDone(false);
    setResults(null);
    setSteps(INITIAL_STEPS);
    setOverallProgress(0);
  };

  const modeOptions = [
    { id: 'low', label: 'Low', desc: 'Slight compression, high quality' },
    { id: 'medium', label: 'Medium', desc: 'Good compression, good quality', recommended: true },
    { id: 'high', label: 'High', desc: 'High compression, standard quality' },
  ];

  /* Step indicator icons (consistent with LockPDF) */
  const StepIcon = ({ status }: { status: StepStatus }) => {
    if (status === 'done')
      return (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: colors.primary }}
        >
          <Icon name="CheckIcon" size={14} className="text-white" />
        </div>
      );
    if (status === 'error')
      return (
        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-red-500">
          <Icon name="XMarkIcon" size={14} className="text-white" />
        </div>
      );
    if (status === 'active')
      return (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: colors.primary }}
        >
          <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
      );
    return (
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-brand-border">
        <div className="w-2 h-2 rounded-full bg-custom-lavender-gray" />
      </div>
    );
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      {!file && (
        <UploadZone
          onFilesSelected={handleFilesSelected}
          multiple={false}
          accentColor={colors.primary}
          iconName="ArchiveBoxIcon"
          title="Drop PDF file here"
          subtitle="or click to browse — single file only"
          buttonText="Select PDF File"
          dragTitle="Drop your PDF here!"
        />
      )}

      {file && !isDone && (
        <div className="flex flex-col lg:flex-row gap-6 mt-4">
          {/* ================= LEFT SIDE ================= */}
          <div className="flex-1 min-w-0">
            {/* File Item */}
            <FileCard file={file} onRemove={handleReset} theme={COMPRESS_THEME} />

            {/* Mode Selector */}
            {!isProcessing && (
              <div className="mb-6 text-left">
                <h4 className="font-heading font-bold text-sm text-brand-dark mb-3">
                  Compression Level
                </h4>
                <div className="grid grid-cols-1 gap-3">
                  {modeOptions.map((opt) => {
                    const isSelected = mode === opt.id;

                    return (
                      <label
                        key={opt.id}
                        className={`relative flex items-center gap-3 p-4 rounded-2xl cursor-pointer transition-all border-2 ${isSelected ? '' : 'bg-white dark:bg-[#1f1f1f] border-[#EEEEF5] dark:border-[#2a2a2a] hover:bg-gray-50 dark:hover:bg-[#262626]'}`}
                        style={
                          isSelected
                            ? {
                                background: colors.surface,
                                borderColor: colors.primary,
                                boxShadow: `0 4px 14px ${colors.shadow}`,
                              }
                            : {}
                        }
                      >
                        <input
                          type="radio"
                          name="compression-mode"
                          className="hidden"
                          checked={isSelected}
                          onChange={() => setMode(opt.id as CompressionMode)}
                        />

                        {/* Radio Indicator */}
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#10B981]' : 'border-[#C4C4D4] dark:border-gray-500'}`}
                        >
                          {isSelected && (
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ background: colors.primary }}
                            />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-heading font-extrabold text-[15px] ${isSelected ? 'text-gray-900' : 'text-brand-dark dark:text-white'}`}
                            >
                              {opt.label}
                            </span>

                            {opt.recommended && (
                              <span
                                className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-white"
                                style={{ background: colors.primary }}
                              >
                                Recommended
                              </span>
                            )}
                          </div>

                          <p
                            className={`font-body text-[12px] ${isSelected ? 'text-gray-700' : 'text-brand-muted dark:text-gray-400'}`}
                          >
                            {opt.desc}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div className="w-full lg:w-[280px]">
            {/* Progress Steps */}
            {isProcessing && (
              <div className="mb-6 animate-in fade-in duration-300">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading font-semibold text-[13px] text-brand-mid dark:text-gray-300">
                    Processing your PDF…
                  </span>
                  <span
                    className="font-heading font-extrabold text-[13px]"
                    style={{ color: colors.primary }}
                  >
                    {Math.round(overallProgress)}%
                  </span>
                </div>

                {/* Progress Bar Background */}
                <div className="w-full h-2.5 rounded-full overflow-hidden bg-slate-100 dark:bg-[#1f1f1f] mb-5 text-left">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${overallProgress}%`,
                      background: `linear-gradient(90deg, ${colors.primary} 0%, #34D399 100%)`,
                    }}
                  />
                </div>

                <div className="space-y-3 text-left">
                  {steps.map((step) => (
                    <div
                      key={step.id}
                      className="flex gap-3 p-3 rounded-xl transition-all duration-300 items-center justify-center"
                      style={{
                        background:
                          step.status === 'active'
                            ? colors.surface
                            : step.status === 'done'
                              ? '#F0FDF4'
                              : step.status === 'error'
                                ? '#FEF2F2'
                                : '',
                        border: `1.5px solid ${
                          step.status === 'active'
                            ? colors.border
                            : step.status === 'done'
                              ? '#BBF7D0'
                              : step.status === 'error'
                                ? '#FECACA'
                                : colors.shadow
                        }`,
                      }}
                    >
                      <StepIcon status={step.status} />

                      <div className="flex-1 min-w-0">
                        <p
                          className="font-heading font-bold text-[13px]"
                          style={{
                            color:
                              step.status === 'done'
                                ? '#10B981'
                                : step.status === 'error'
                                  ? '#DC2626'
                                  : step.status === 'active'
                                    ? '#1A1A2E'
                                    : 'white',
                          }}
                        >
                          {step.label}
                        </p>

                        {(step.status === 'active' || step.status === 'done') && (
                          <p className="font-body text-[12px] text-brand-muted dark:text-gray-400 mt-0.5">
                            {step.detail}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compress Button */}
            {!isProcessing && (
              <button
                onClick={handleCompress}
                className="w-full py-4 rounded-2xl font-heading font-extrabold text-white transition-all hover:translate-y-[-2px] active:translate-y-0"
                style={{
                  background: colors.primary,
                  boxShadow: colors.shadow,
                }}
              >
                <span className="flex items-center justify-center gap-2">Compress PDF</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Success State */}
      {isDone && results && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 pulse-anim bg-green-50">
            <Icon name="CheckBadgeIcon" size={42} variant="solid" className="text-green-600" />
          </div>

          <h3 className="font-heading font-bold mb-2 text-[20px] text-brand-dark">
            Compression Complete!
          </h3>
          <p className="text-brand-mid text-[14px] font-body mb-6">
            Your file <strong>{file?.name}</strong> has been reduced by{' '}
            <strong>{results?.reductionPercent}</strong>.
          </p>

          {/* Size Comparison Card */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="p-4 rounded-2xl border border-brand-border text-brand-mid hover:bg-brand-surface transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2">
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1">Before</p>

              <p className="font-extrabold text-lg">{results?.originalSize}</p>
            </div>
            <div
              className="p-4 rounded-2xl border"
              style={{ background: colors.surface, borderColor: colors.border }}
            >
              <p
                className="text-[10px] font-bold uppercase tracking-wider mb-1"
                style={{ color: colors.primary }}
              >
                After
              </p>
              <p
                className="font-heading font-extrabold text-lg text-brand-dark"
                style={{ color: colors.primary }}
              >
                {results?.compressedSize}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={results?.pdfUrl}
              download
              className="sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary text-white border-none font-heading font-bold text-[15px] cursor-pointer shadow-[0_6px_20px_rgba(232,68,90,0.28)]"
              style={{
                background: colors.primary,
                boxShadow: colors.shadow,
              }}
            >
              <Icon name="ArrowDownTrayIcon" size={18} />
              Download Compressed PDF
            </a>
            <button
              onClick={handleReset}
              className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
              aria-label="Start a new split task"
            >
              <Icon name="ArrowPathIcon" size={18} />
              Compress Another PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
